// SCR-STAFF-402 — شیفت صندوق (FLOW-06؛ US-BIL-035، 041، 042، 043): باز کردن با موجودی اولیه و مقایسه با پایانی شیفت قبل، پرداخت‌های صندوق (تنخواه با
// سقف و تأیید مدیر)، شمارش کور (نقد، کارتخوان، چک) ← نمایش مورد انتظار و مغایرت هر روش، بستن با دلیل مغایرت (بیش از سقف ← «در انتظار تأیید مدیر»)
// و فهرست شیفت‌ها با تأیید مدیر (bil.cash_session.approve). AP-BIL-04: صف کارتخوان نامعلوم و واریز نقد به بانک (US-BIL-032، 044).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type BilCashSessionListItem, type BilCashSessionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, JalaliDate, KeyValueList, LoadingState, PageHeader, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { useApproval } from '../iam/useApproval';
import { BankDepositsPanel, PendingPaymentsPanel } from './CashFinancePanels';
import { parseRials } from './CashierPage';
import { Amount, BIL_CASHIER_PATH, bilKeys, num, rial, SCR_STAFF_402, SessionStatusChip } from './common';

export function CashSessionPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const current = useQuery({ queryKey: bilKeys.session(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-sessions/current')) });
  // شیفت تازه بسته‌شده (Closed/Discrepancy) دیگر «جاری» نیست؛ نتیجه بستن تا ترک صفحه نمایش داده می‌شود.
  const [closed, setClosed] = useState<BilCashSessionView | null>(null);
  const refresh = (view?: BilCashSessionView) => {
    if (view) {
      queryClient.setQueryData(bilKeys.sessionDetail(session?.tenantId, view.id), view);
      setClosed(view.status === 'Open' || view.status === 'Closing' ? null : view);
    }
    void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
  };

  if (current.isPending) {
    return <LoadingState rows={6} />;
  }
  if (current.error || !current.data) {
    return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  }
  const shift = current.data.session;
  return (
    <Stack data-screen-id={SCR_STAFF_402} spacing={2}>
      <PageHeader
        titleKey="bil:session.title"
        badges={shift ? <SessionStatusChip status={shift.status} /> : undefined}
        actions={
          <Button component={RouterLink} to={BIL_CASHIER_PATH} variant="outlined">
            {t('bil:cashier.title')}
          </Button>
        }
      />
      {!shift && closed ? <ShiftPanel shift={closed} onChange={refresh} /> : null}
      {shift ? <ShiftPanel shift={shift} onChange={refresh} /> : access.can('bil.cash_session.open') ? <OpenShift registers={current.data.registers} onOpened={refresh} /> : null}
      {access.accessMode === 'Full' && access.can('bil.cash_session.approve') ? (
        <>
          <PendingPaymentsPanel />
          <BankDepositsPanel />
        </>
      ) : null}
      {access.can('bil.cash_session.view_all') ? <SessionsList /> : null}
    </Stack>
  );
}

function OpenShift({ registers, onOpened }: { registers: { id: string; name: string; hasOpenSession: boolean; isActive: boolean; previousClosing: { amount: number; currency: string } | null }[]; onOpened: (view: BilCashSessionView) => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const available = registers.filter((register) => register.isActive && !register.hasOpenSession);
  const [registerId, setRegisterId] = useState(available[0]?.id ?? '');
  const selected = registers.find((register) => register.id === registerId);
  const [float, setFloat] = useState(selected?.previousClosing ? String(selected.previousClosing.amount) : '');
  const open = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/cash-sessions', {
          body: { registerId, openingFloat: rial(parseRials(float)) },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: onOpened,
  });
  if (registers.length === 0) {
    return <Alert severity="info">{t('bil:session.no_registers')}</Alert>;
  }
  const previous = selected?.previousClosing?.amount;
  const differs = previous !== undefined && float !== '' && parseRials(float) !== previous;
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2} sx={{ maxWidth: 420 }}>
        <Typography variant="subtitle1">{t('bil:session.open_title')}</Typography>
        <TextField
          select
          label={t('bil:session.register')}
          value={registerId}
          onChange={(event) => {
            setRegisterId(event.target.value);
            const next = registers.find((register) => register.id === event.target.value);
            setFloat(next?.previousClosing ? String(next.previousClosing.amount) : '');
          }}
          slotProps={{ htmlInput: { 'data-testid': 'bil-session-register' } }}
        >
          {available.map((register) => (
            <MenuItem key={register.id} value={register.id}>
              {register.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label={t('bil:session.opening_float')}
          value={float}
          onChange={(event) => setFloat(event.target.value)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-session-float' } }}
          helperText={previous !== undefined ? <span>{t('bil:session.previous_closing')} <Amount money={rial(previous)} /></span> : undefined}
        />
        {differs ? <Alert severity="warning" data-testid="bil-session-float-diff">{t('bil:session.float_differs')}</Alert> : null}
        {open.error ? <ErrorState error={open.error} compact /> : null}
        <Button variant="contained" onClick={() => open.mutate()} disabled={!registerId || float === '' || open.isPending} data-testid="bil-session-open">
          {t('bil:session.open')}
        </Button>
      </Stack>
    </Paper>
  );
}

function ShiftPanel({ shift, onChange }: { shift: BilCashSessionView; onChange: (view?: BilCashSessionView) => void }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const actionKey = useIdempotencyKey();
  const pettyKey = useIdempotencyKey();
  const { requireApproval, dialog: approvalDialog } = useApproval();
  const detail = useQuery({
    queryKey: bilKeys.sessionDetail(session?.tenantId, shift.id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-sessions/{id}', { params: { path: { id: shift.id } } })),
    initialData: shift,
  });
  const view = detail.data;
  const settings = useQuery({ queryKey: bilKeys.settings(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/settings')), enabled: access.can('bil.petty_cash.manage') });
  const [cash, setCash] = useState('');
  const [card, setCard] = useState('');
  const [cheques, setCheques] = useState('0');
  const [reason, setReason] = useState('');
  const [pettyOpen, setPettyOpen] = useState(false);
  const [petty, setPetty] = useState({ amount: '', category: '', payee: '', description: '' });
  const count = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/cash-sessions/{id}:count', {
          params: { path: { id: shift.id } },
          body: { countedCash: rial(parseRials(cash)), countedCard: rial(parseRials(card)), chequeCount: Number(cheques) || 0, denominations: null },
        }),
      ),
    onSuccess: onChange,
  });
  const close = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/cash-sessions/{id}:close', {
          params: { path: { id: shift.id } },
          body: { reason: reason.trim() || null },
          headers: { 'Idempotency-Key': actionKey.current() },
        }),
      ),
    onSettled: (_data, error) => actionKey.settle(error ?? undefined),
    onSuccess: onChange,
  });
  const addPetty = useMutation({
    mutationFn: async () => {
      const body = {
        amount: rial(parseRials(petty.amount)),
        category: petty.category,
        payee: petty.payee.trim(),
        description: petty.description.trim() || null,
        documentId: null,
        approvalId: null as string | null,
      };
      const send = () =>
        unwrap(
          tenantApi.POST('/api/v1/billing/cash-sessions/{id}/petty-cash', {
            params: { path: { id: shift.id } },
            body,
            headers: { 'Idempotency-Key': pettyKey.current() },
          }),
        );
      try {
        return await send();
      } catch (error) {
        if (isApiError(error) && error.code === 'PHX-BIL-0209' && typeof error.extensions['key'] === 'string') {
          pettyKey.settle(error);
          const outcome = await requireApproval({ key: error.extensions['key'] as string, amount: body.amount.amount });
          if (outcome.status === 'approved' || outcome.status === 'requested') {
            body.approvalId = outcome.approvalId;
            return send();
          }
        }
        throw error;
      }
    },
    onSettled: (_data, error) => pettyKey.settle(error ?? undefined),
    onSuccess: (next) => {
      setPettyOpen(false);
      setPetty({ amount: '', category: '', payee: '', description: '' });
      onChange(next);
    },
  });
  const counted = view.countedAt !== null;
  const discrepancy = num(view.totalDiscrepancy?.amount ?? 0) !== 0;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <KeyValueList
          items={[
            { labelKey: 'bil:session.register', value: view.registerName ?? '—' },
            { labelKey: 'bil:session.cashier', value: view.cashierName ?? '—' },
            { labelKey: 'bil:session.opened_at', value: <JalaliDate value={view.openedAt} withTime /> },
            { labelKey: 'bil:session.opening_float', value: <Amount money={view.openingFloat} /> },
            { labelKey: 'bil:session.opening_difference', value: <Amount money={view.openingDifference} testId="bil-session-opening-diff" /> },
          ]}
        />
        {num(view.unresolvedPayments) > 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="bil-session-unresolved">
            {t('bil:session.unresolved', { n: num(view.unresolvedPayments) })}
          </Alert>
        ) : null}
        {view.blindCount && !counted ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="bil-session-blind">
            {t('bil:session.blind_hint')}
          </Typography>
        ) : null}
      </Paper>

      {view.pettyCash.length > 0 || access.can('bil.petty_cash.manage') ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1">{t('bil:session.petty_title')}</Typography>
            {access.can('bil.petty_cash.manage') && view.status === 'Open' ? (
              <Button onClick={() => setPettyOpen(true)} data-testid="bil-petty-add">
                {t('bil:session.petty_add')}
              </Button>
            ) : null}
          </Stack>
          {view.pettyCash.map((entry) => (
            <Stack key={entry.id} direction="row" spacing={2} data-testid="bil-petty-row">
              <Typography>{t(`bil:petty_category.${entry.category}`, { defaultValue: entry.category })}</Typography>
              <Typography>{entry.payee}</Typography>
              <Amount money={entry.amount} />
            </Stack>
          ))}
        </Paper>
      ) : null}

      {view.status === 'Open' || view.status === 'Closing' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            <Typography variant="subtitle1">{t('bil:session.count_title')}</Typography>
            <TextField label={t('bil:session.counted_cash')} value={cash} onChange={(event) => setCash(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-count-cash' } }} />
            <TextField label={t('bil:session.counted_card')} value={card} onChange={(event) => setCard(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-count-card' } }} />
            <TextField label={t('bil:session.cheques')} value={cheques} onChange={(event) => setCheques(event.target.value.replace(/\D/g, ''))} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }} />
            {count.error ? <ErrorState error={count.error} compact /> : null}
            <Button variant="outlined" onClick={() => count.mutate()} disabled={cash === '' || card === '' || count.isPending} data-testid="bil-count-submit">
              {t('bil:session.count')}
            </Button>
          </Stack>
        </Paper>
      ) : null}

      {view.counts ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Table size="small" aria-label={t('bil:session.counts')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('bil:session.method')}</TableCell>
                <TableCell>{t('bil:session.expected')}</TableCell>
                <TableCell>{t('bil:session.counted')}</TableCell>
                <TableCell>{t('bil:session.difference')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.counts.map((row) => (
                <TableRow key={row.method} data-testid="bil-count-row" data-method={row.method}>
                  <TableCell>{t(`bil:method.${row.method}`)}</TableCell>
                  <TableCell>
                    <Amount money={row.expected} testId="bil-count-expected" />
                  </TableCell>
                  <TableCell>{counted ? <Amount money={row.counted} /> : '—'}</TableCell>
                  <TableCell>{counted ? <Amount money={row.difference} testId="bil-count-difference" /> : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}

      {view.status === 'Closing' ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            {discrepancy ? (
              <TextField
                required
                label={t('bil:session.reason')}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                slotProps={{ htmlInput: { 'data-testid': 'bil-close-reason' } }}
              />
            ) : null}
            {close.error ? <ErrorState error={close.error} compact /> : null}
            <Button variant="contained" onClick={() => close.mutate()} disabled={close.isPending || (discrepancy && reason.trim() === '')} data-testid="bil-close-submit">
              {t('bil:session.close')}
            </Button>
          </Stack>
        </Paper>
      ) : null}
      {view.status === 'Discrepancy' ? (
        <Alert severity="warning" data-testid="bil-session-awaiting">
          {t('bil:session.awaiting_approval')}
        </Alert>
      ) : null}
      {view.status === 'Closed' || view.status === 'Approved' ? (
        <Alert severity="success" data-testid="bil-session-closed">
          {t('bil:session.closed')}
        </Alert>
      ) : null}

      <FormDialog
        open={pettyOpen}
        titleKey="bil:session.petty_add"
        onSubmit={() => addPetty.mutate()}
        onClose={() => setPettyOpen(false)}
        isDirty={petty.amount !== '' || petty.payee !== ''}
        isSubmitting={addPetty.isPending}
        submitError={addPetty.error}
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label={t('bil:session.petty_amount')} value={petty.amount} onChange={(event) => setPetty({ ...petty, amount: event.target.value })} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-petty-amount' } }} />
          <TextField
            select
            label={t('bil:session.petty_category')}
            value={petty.category}
            onChange={(event) => setPetty({ ...petty, category: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'bil-petty-category' } }}
          >
            {(settings.data?.pettyCashCategories ?? []).map((category) => (
              <MenuItem key={category} value={category}>
                {t(`bil:petty_category.${category}`, { defaultValue: category })}
              </MenuItem>
            ))}
          </TextField>
          <TextField label={t('bil:session.petty_payee')} value={petty.payee} onChange={(event) => setPetty({ ...petty, payee: event.target.value })} slotProps={{ htmlInput: { 'data-testid': 'bil-petty-payee' } }} />
          <TextField label={t('bil:session.petty_description')} value={petty.description} onChange={(event) => setPetty({ ...petty, description: event.target.value })} />
        </Stack>
      </FormDialog>
      {approvalDialog}
    </Stack>
  );
}

function SessionsList() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const approveKey = useIdempotencyKey();
  const sessions = useInfiniteQuery({
    queryKey: bilKeys.sessions(session?.tenantId, 'all'),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => unwrap(tenantApi.GET('/api/v1/billing/cash-sessions', { params: { query: { cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const approve = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/cash-sessions/{id}:approve', {
          params: { path: { id } },
          body: { note: null },
          headers: { 'Idempotency-Key': approveKey.current() },
        }),
      ),
    onSettled: (_data, error) => approveKey.settle(error ?? undefined),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) }),
  });
  const canApprove = access.accessMode === 'Full' && access.can('bil.cash_session.approve');
  const columns: DataTableColumn<BilCashSessionListItem>[] = [
    { id: 'date', headerKey: 'bil:session.opened_at', cell: (row) => <JalaliDate value={row.openedAt} withTime /> },
    { id: 'register', headerKey: 'bil:session.register', cell: (row) => row.registerName ?? '—' },
    { id: 'cashier', headerKey: 'bil:session.cashier', cell: (row) => row.cashierName ?? '—' },
    { id: 'diff', headerKey: 'bil:session.difference', cell: (row) => <Amount money={row.totalDiscrepancy} /> },
    { id: 'status', headerKey: 'bil:session.status', cell: (row) => <SessionStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'bil:session.actions',
      cell: (row) =>
        canApprove && (row.status === 'Closed' || row.status === 'Discrepancy') ? (
          <Button size="small" onClick={() => approve.mutate(row.id)} disabled={approve.isPending} data-testid="bil-session-approve">
            {t('bil:session.approve')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">{t('bil:session.list_title')}</Typography>
      {approve.error ? <ErrorState error={approve.error} compact /> : null}
      <DataTable
        captionKey="bil:session.list_title"
        columns={columns}
        rows={sessions.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={sessions.isPending}
        error={sessions.error}
        onRetry={() => void sessions.refetch()}
        hasMore={sessions.hasNextPage}
        onLoadMore={() => void sessions.fetchNextPage()}
        isFetchingMore={sessions.isFetchingNextPage}
        emptyTitleKey="bil:session.list_empty"
      />
    </Stack>
  );
}
